import { useState, useEffect, useCallback } from 'react';
import {
  getWorkflows,
  getPublicWorkflows,
  clonePreviewWorkflow,
  crossAgencyCloneWorkflow as crossAgencyCloneService,
  getMacSsoToken,
  type Workflow,
  type PublicWorkflow,
  type WorkflowMeta
} from '../services/workflowService';
import { FusionNowNotSubscribedError } from '../services/fusionNowAuth';

export function useWorkflows({ perPage = 12 }: { perPage?: number } = {}) {
  // ── My Workflows ──────────────────────────────────────────────────────────
  const [workflows, setWorkflows] = useState<Workflow[]>([]);
  const [meta, setMeta] = useState<WorkflowMeta | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // ── Public Workflows ──────────────────────────────────────────────────────
  const [publicWorkflows, setPublicWorkflows] = useState<PublicWorkflow[]>([]);
  const [publicMeta, setPublicMeta] = useState<WorkflowMeta | null>(null);
  const [isLoadingPublic, setIsLoadingPublic] = useState(false);
  const [publicError, setPublicError] = useState<string | null>(null);

  // ── Shared search ─────────────────────────────────────────────────────────
  const [searchQuery, setSearchQuery] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');

  // ── Clone state (public workflows) ────────────────────────────────────────
  const [isCloning, setIsCloning] = useState<string | null>(null);
  const [cloneSuccess, setCloneSuccess] = useState<string | null>(null);
  const [cloneError, setCloneError] = useState<string | null>(null);

  // ── View In MAC state (my workflows) ─────────────────────────────────────
  const [isViewingInMac, setIsViewingInMac] = useState<string | null>(null);
  const [viewInMacError, setViewInMacError] = useState<string | null>(null);
  const [notSubscribed, setNotSubscribed] = useState(false);

  useEffect(() => {
    const timer = setTimeout(() => setDebouncedSearch(searchQuery.trim()), 400);
    return () => clearTimeout(timer);
  }, [searchQuery]);

  const fetchPage = useCallback(async (page: number, search: string) => {
    setIsLoading(true);
    setError(null);
    try {
      const { workflows: data, meta: m } = await getWorkflows(page, search, perPage);
      setWorkflows(data);
      setMeta(m);
    } catch (err) {
      if (err instanceof FusionNowNotSubscribedError) {
        setNotSubscribed(true);
      } else {
        setError(err instanceof Error ? err.message : 'Failed to load workflows');
      }
    } finally {
      setIsLoading(false);
    }
  }, [perPage]);

  const fetchPublicPage = useCallback(async (page: number, search: string) => {
    setIsLoadingPublic(true);
    setPublicError(null);
    try {
      const { workflows: data, meta: m } = await getPublicWorkflows(page, search, perPage);
      setPublicWorkflows(data);
      setPublicMeta(m);
    } catch (err) {
      setPublicError(
        err instanceof Error ? err.message : 'Failed to load public workflows'
      );
    } finally {
      setIsLoadingPublic(false);
    }
  }, [perPage]);

  // Fetch page 1 for both sections whenever debouncedSearch changes (covers initial load)
  useEffect(() => {
    fetchPage(1, debouncedSearch);
    fetchPublicPage(1, debouncedSearch);
  }, [debouncedSearch, fetchPage, fetchPublicPage]);

  const fetchWorkflows = useCallback(() => {
    fetchPage(meta?.current_page ?? 1, debouncedSearch);
  }, [fetchPage, debouncedSearch, meta]);

  const fetchPublicWorkflows = useCallback(() => {
    fetchPublicPage(publicMeta?.current_page ?? 1, debouncedSearch);
  }, [fetchPublicPage, debouncedSearch, publicMeta]);

  const goToPage = useCallback(
    (page: number) => {
      setSearchQuery('');
      setDebouncedSearch('');
      fetchPage(page, '');
    },
    [fetchPage]
  );

  const goToPublicPage = useCallback(
    (page: number) => {
      setSearchQuery('');
      setDebouncedSearch('');
      fetchPublicPage(page, '');
    },
    [fetchPublicPage]
  );

  const cloneWorkflow = useCallback(async (key: string) => {
    setIsCloning(key);
    setCloneError(null);
    try {
      const preview = await clonePreviewWorkflow(key);

      if (preview.missing_entities.length === 0) {
        await crossAgencyCloneService(key);
        setCloneSuccess(key);
        setTimeout(() => setCloneSuccess(null), 2000);
      } else {
        const amsToken = localStorage.getItem('amsToken') ?? '';
        const ssoToken = await getMacSsoToken(amsToken);
        window.open(
          `${process.env.REACT_APP_FUSIONNOW_APP_URL}/now-certs-auth?token=${encodeURIComponent(ssoToken)}&action=workflowdetail-${key}-clone`,
          '_blank'
        );
      }
    } catch {
      setCloneError(key);
      setTimeout(() => setCloneError(null), 3000);
    } finally {
      setIsCloning(null);
    }
  }, []);

  const viewInMac = useCallback(async (key: string) => {
    setIsViewingInMac(key);
    setViewInMacError(null);
    try {
      const amsToken = localStorage.getItem('amsToken') ?? '';
      const ssoToken = await getMacSsoToken(amsToken);
      window.open(
        `${process.env.REACT_APP_FUSIONNOW_APP_URL}/now-certs-auth?token=${encodeURIComponent(ssoToken)}&action=workflowdetail-${key}`,
        '_blank'
      );
    } catch {
      setViewInMacError(key);
      setTimeout(() => setViewInMacError(null), 3000);
    } finally {
      setIsViewingInMac(null);
    }
  }, []);

  return {
    workflows,
    meta,
    isLoading,
    error,
    publicWorkflows,
    publicMeta,
    isLoadingPublic,
    publicError,
    notSubscribed,
    searchQuery,
    setSearchQuery,
    isCloning,
    cloneSuccess,
    cloneError,
    isViewingInMac,
    viewInMacError,
    fetchWorkflows,
    fetchPublicWorkflows,
    goToPage,
    goToPublicPage,
    cloneWorkflow,
    viewInMac
  };
}
